import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Image from 'components/Common/Image'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getCartItemDisplayPrice } from '../../utils/cartItemPriceSelection'
import type { CartPanelItem } from '../../hooks/useCartPanelItems'
import { CHAT_CART_ROW_ACTION, resolveChatCartRowAction, type ChatCartRowAction } from './chatCartItems'
import { useChatCartRowSummary } from './useChatCartRowSummary'

const IMAGE_SIZE = 40

const Row = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${rem(12)};
  padding-block: ${rem(10)};

  /* Matches CartPanelItemTileLayout: an item the customer deselected in the cart is
     still listed, but must not read as something the book button will charge for. */
  &.deselected {
    opacity: 0.5;
  }
`

const Thumbnail = styled.div`
  width: ${rem(IMAGE_SIZE)};
  height: ${rem(IMAGE_SIZE)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  flex-shrink: 0;
  background: ${props => props.theme.palette.neutral.default.six};
`

/**
 * Matches the saved flight tile (ShortlistedFlightLegRow): the carrier logo is a plain
 * external URL, so it renders as a raw img rather than through Image's id pipeline.
 */
const AirlineLogo = styled.img`
  width: ${rem(IMAGE_SIZE)};
  height: ${rem(IMAGE_SIZE)};
  object-fit: contain;
  flex-shrink: 0;
`

const Details = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const ClampedText = styled(BodyText)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const BlockerText = styled(BodyText)`
  color: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
`

const Trailing = styled.div`
  flex-shrink: 0;
  text-align: end;
  padding-block-start: ${rem(2)};
`

interface Props {
  item: CartPanelItem
  isDisabled: boolean
  isRefreshing: boolean
  onOpenOffer: (item: CartPanelItem) => void
  onChooseTickets: (item: CartPanelItem) => void
  onChooseDifferentTickets: (item: CartPanelItem) => void
  onChooseDate: (item: CartPanelItem) => void
  onChooseTime: (item: CartPanelItem) => void
  onChooseFlight: (item: CartPanelItem) => void
  onRefreshFlight: (item: CartPanelItem) => void
  onChangeFlight: (item: CartPanelItem) => void
}

function ChatCartRow({
  item,
  isDisabled,
  isRefreshing,
  onOpenOffer,
  onChooseTickets,
  onChooseDifferentTickets,
  onChooseDate,
  onChooseTime,
  onChooseFlight,
  onRefreshFlight,
  onChangeFlight,
}: Props) {
  const intl = useIntl()
  const hasLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const { meta, statusLabel } = useChatCartRowSummary(item)
  const action = resolveChatCartRowAction(item)

  const handleActionClick = useCallback(() => {
    switch (action) {
      case CHAT_CART_ROW_ACTION.ADD_DATES:
      case CHAT_CART_ROW_ACTION.CHOOSE_DATES:
      case CHAT_CART_ROW_ACTION.CHOOSE_PACKAGE: return onOpenOffer(item)
      case CHAT_CART_ROW_ACTION.CHOOSE_TICKETS: return onChooseTickets(item)
      case CHAT_CART_ROW_ACTION.CHOOSE_DIFFERENT_TICKETS: return onChooseDifferentTickets(item)
      case CHAT_CART_ROW_ACTION.CHOOSE_DATE: return onChooseDate(item)
      case CHAT_CART_ROW_ACTION.CHOOSE_TIME: return onChooseTime(item)
      case CHAT_CART_ROW_ACTION.CHOOSE_FLIGHT: return onChooseFlight(item)
      case CHAT_CART_ROW_ACTION.REFRESH_FLIGHT: return onRefreshFlight(item)
      case CHAT_CART_ROW_ACTION.CHANGE_FLIGHT: return onChangeFlight(item)
    }
  }, [
    action,
    item,
    onOpenOffer,
    onChooseTickets,
    onChooseDifferentTickets,
    onChooseDate,
    onChooseTime,
    onChooseFlight,
    onRefreshFlight,
    onChangeFlight,
  ])

  const actionLabels: Record<ChatCartRowAction, string> = useMemo(() => ({
    [CHAT_CART_ROW_ACTION.ADD_DATES]: intl.formatMessage({
      id: 'pIU_0ToAxMQWjf3Qq19E',
      defaultMessage: 'Add dates',
      description: 'Link on a compact chat cart row that opens the hotel offer so the customer can add stay dates',
    }),
    [CHAT_CART_ROW_ACTION.CHOOSE_DATES]: intl.formatMessage({
      id: 'V2LE0eq7SuU6LUpelr-n',
      defaultMessage: 'Choose dates',
      description: 'Action button that opens the hotel drawer so the user can choose dates for a duration-unavailable hotel',
    }),
    [CHAT_CART_ROW_ACTION.CHOOSE_PACKAGE]: intl.formatMessage({
      id: '9Tle--nXWQetSKbmQ8kA',
      defaultMessage: 'Choose package',
      description: 'Action button that opens the hotel drawer so the user can choose a package for checkout',
    }),
    [CHAT_CART_ROW_ACTION.CHOOSE_TICKETS]: intl.formatMessage({
      id: 'njbeFVr3fkEqMDg6oMfp',
      defaultMessage: 'Choose tickets',
      description: 'Link on a compact chat cart row that lets the customer choose tickets for an experience',
    }),
    [CHAT_CART_ROW_ACTION.CHOOSE_DIFFERENT_TICKETS]: intl.formatMessage({
      id: 'HCJuZHkco8smpGLU6qkI',
      defaultMessage: 'Choose different tickets',
      description: 'Link on a compact chat cart row that lets the customer replace unavailable experience tickets',
    }),
    [CHAT_CART_ROW_ACTION.CHOOSE_DATE]: intl.formatMessage({
      id: 'LkowovNFE2S7fefA7mUQ',
      defaultMessage: 'Choose date',
      description: 'Link on a compact chat cart row that lets the customer choose a date for an experience',
    }),
    [CHAT_CART_ROW_ACTION.CHOOSE_TIME]: intl.formatMessage({
      id: 'Ow9biPWvw-4Ltml1X18y',
      defaultMessage: 'Choose time',
      description: 'Link on a compact chat cart row that lets the customer choose a time for an experience',
    }),
    [CHAT_CART_ROW_ACTION.CHOOSE_FLIGHT]: intl.formatMessage({
      id: 'wlZW6lZEh_0pAjvLOeNZ',
      defaultMessage: 'Choose flight',
      description: 'Link on a compact chat cart row that lets the customer choose a flight for this connection',
    }),
    [CHAT_CART_ROW_ACTION.REFRESH_FLIGHT]: intl.formatMessage({
      id: 'Jh-CAGCQUEuBbstfiO9X',
      defaultMessage: 'Refresh search',
      description: 'Button label to refresh an expired TripChat cart flight',
    }),
    [CHAT_CART_ROW_ACTION.CHANGE_FLIGHT]: intl.formatMessage({
      id: '_UqqvrffchEPRogC5KWI',
      defaultMessage: 'Search for flights',
      description: 'Button label to search for a replacement TripChat cart flight',
    }),
  }), [intl])

  const refreshingLabel = intl.formatMessage({
    id: 'JQLSXoSYTl97kDRzfHt_',
    defaultMessage: 'Refreshing…',
    description: 'Placeholder shown on a chat cart flight row while its expired fare is being refreshed',
  })

  const price = getCartItemDisplayPrice(item.pricing, hasLuxPlusBenefits)
  const carrier = item.productType === 'flight' ? item.savedItemData : undefined

  // One slot, mutually exclusive: an action to take, else why it can't be booked, else
  // what it costs. When an action takes the slot the reason moves under the name, so a
  // row never offers "Choose dates" without saying why the current ones stopped working.
  function renderTrailing() {
    if (action) {
      return (
        <TextLink size="caption" onClick={handleActionClick} disabled={isDisabled || isRefreshing}>
          {isRefreshing ? refreshingLabel : actionLabels[action]}
        </TextLink>
      )
    }
    if (statusLabel) {
      return <BodyText variant="small" colour="neutral-three">{statusLabel}</BodyText>
    }
    if (item.pricing.loading) {
      return <TextLoadingBox typography="body-medium" width="6ch" />
    }
    if (price !== undefined) {
      return <PriceRowPrice price={price} priceFormat="preciseDollar" saleUnit="" size="S" />
    }
    return null
  }

  return (
    <Row className={clsx({ deselected: item.cartExcluded })}>
      {carrier?.carrierLogo && (
        <AirlineLogo src={carrier.carrierLogo} alt={carrier.airline ?? ''} />
      )}
      {!carrier?.carrierLogo && item.imageId && (
        <Thumbnail>
          <Image id={item.imageId} width={IMAGE_SIZE} height={IMAGE_SIZE} fit="center" objectFit="cover" dpr={2} />
        </Thumbnail>
      )}
      <Details>
        <ClampedText variant="medium" weight="bold">{item.name}</ClampedText>
        {meta && <BodyText variant="small" colour="neutral-three">{meta}</BodyText>}
        {action && statusLabel && (
          <BlockerText variant="small" weight="bold">{statusLabel}</BlockerText>
        )}
      </Details>
      <Trailing>{renderTrailing()}</Trailing>
    </Row>
  )
}

export default ChatCartRow
